import { inject, Injectable } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  pairwise,
  startWith,
  switchMap,
} from 'rxjs';
import { selectGlobalMainDataIsLoaded } from '@app/main/main-screen/store/main-data/main-data.selectors';
import { shareReplay, tap, withLatestFrom } from 'rxjs/operators';
import { selectAllCorrelations } from '@app/main/main-screen/store/data/correlations/correlations.selectors';
import { LdTabs } from '@app/main/leak-detection/enums/ld-tabs';
import { Store } from '@ngrx/store';
import { LdCoordinatorStore } from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.store';
import { DataSelectionService } from '@app/main/main-screen/services/data-selection.service';
import { ITimeSelection, ITimeSelectionItem } from '@app/main/main-screen/interfaces/time-selection';
import { LdDataAggregationService } from '@app/main/leak-detection/services/leak-detection-data-aggregation/leak-detection-data-aggregation.service';
import { ILdLogger, ILdLoggerMapByDeploymentId } from '@app/main/leak-detection/interfaces/ld-logger';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ILoggerMeasurement } from '@app/api/interfaces/logger-measurement.interface';
import { TimeSelectionKey } from '@app/main/main-screen/enums/time-selection-key';
import equal from 'fast-deep-equal';
import { IProject } from '@app/api/interfaces/project.interface';
import { isMixedOperatingType } from '@app/core/enums/operating-type.enum';
import { selectAllLoggerMeasurements } from '@app/main/main-screen/store/data/logger-measurements/logger-measurements.selectors';

type ITabTimesMap = Partial<Record<LdTabs, Set<string>>>;

const NO_SOUND_SIGNAL_KEY = 'no-sound-signal';

@Injectable({ providedIn: 'root' })
export class LdDataTimedService {
  private readonly store = inject(Store);
  private ldCoordinatorStore = inject(LdCoordinatorStore);
  private readonly dataSelectionService = inject(DataSelectionService);
  private readonly ldDataAggregationService = inject(LdDataAggregationService);

  private readonly tabDisplayKeyMap: Partial<Record<LdTabs, TimeSelectionKey>> = {
    [LdTabs.Correlations]: TimeSelectionKey.SoundSignalStartTime,
    [LdTabs.LoggerNoise]: TimeSelectionKey.MeasStartTime,
  };

  private readonly tabTimesMap$: Observable<ITabTimesMap | null> = this.store.select(selectGlobalMainDataIsLoaded).pipe(
    takeUntilDestroyed(),
    switchMap(isGlobalDataLoaded => this.dataSelectionService.timeSelection.pipe(filter(() => isGlobalDataLoaded))),
    // outside the `switchMap`, which would restart it on every load
    distinctUntilChanged((prev, curr) => equal(prev?.selections, curr?.selections)),
    map(timeSelection => this.buildTabTimesMap(timeSelection)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly correlations$ = combineLatest([this.tabTimesMap$, this.store.select(selectAllCorrelations)]).pipe(
    map(([tabTimesMap, correlations]) => {
      return !tabTimesMap
        ? correlations
        : correlations.filter(correlation =>
            this.isRelatedToSelectedTime(
              tabTimesMap,
              correlation.soundSignalStartAt,
              LdTabs.Correlations,
              NO_SOUND_SIGNAL_KEY
            )
          );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly ldLoggers$ = combineLatest([this.tabTimesMap$, this.ldDataAggregationService.ldLoggers$]).pipe(
    map(([tabTimesMap, ldLoggers]) => {
      if (!tabTimesMap) {
        return ldLoggers;
      }

      const { matched, unmatched, usedDeploymentIds } = ldLoggers.reduce(
        (acc, ldLogger) => {
          const deploymentId = ldLogger.deployment.id;

          const isMatched = this.isRelatedToSelectedTime(
            tabTimesMap,
            ldLogger.measurement?.measStartTime,
            LdTabs.LoggerNoise
          );

          if (isMatched) {
            acc.matched.push(ldLogger);
            acc.usedDeploymentIds.add(deploymentId);
          } else {
            acc.unmatched.push(ldLogger);
          }

          return acc;
        },
        {
          matched: [] as typeof ldLoggers,
          unmatched: [] as typeof ldLoggers,
          usedDeploymentIds: new Set<string>(),
        }
      );

      return [
        ...matched,
        ...unmatched.reduce<typeof ldLoggers>((acc, ldLogger) => {
          const deploymentId = ldLogger.deployment.id;

          if (!usedDeploymentIds.has(deploymentId)) {
            usedDeploymentIds.add(deploymentId);
            acc.push({
              ...ldLogger,
              measurement: undefined,
            });
          }

          return acc;
        }, []),
      ];
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly ldLoggersMapByDeploymentId$: Observable<ILdLoggerMapByDeploymentId> = this.ldLoggers$.pipe(
    map(ldLoggers =>
      ldLoggers.reduce((acc, logger) => {
        if (!acc[logger.deployment.id]) {
          acc[logger.deployment.id] = [];
        }
        acc[logger.deployment.id].push(logger);

        return acc;
      }, {} as ILdLoggerMapByDeploymentId)
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public init$() {
    const timeSelectionItems$ = this.store.select(selectGlobalMainDataIsLoaded).pipe(
      switchMap(loaded =>
        loaded ? this.store.select(selectAllLoggerMeasurements).pipe(map(this.buildTimeSelectionItems)) : of([])
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const syncItems$ = timeSelectionItems$.pipe(tap(items => this.dataSelectionService.setTimeSelectionItems(items)));

    const tabSelection$ = combineLatest([
      this.store.select(selectGlobalMainDataIsLoaded),
      this.ldCoordinatorStore.activeTab$,
    ]).pipe(
      startWith([false, null] as [boolean, LdTabs | null]),
      pairwise(),
      map(([[prevLoaded, prevTab], [loaded, tab]]) => ({
        loaded,
        tab,
        loadedChanged: prevLoaded !== loaded,
        tabChanged: prevTab !== tab,
      })),
      filter(({ loaded, loadedChanged, tabChanged }) => loaded && (loadedChanged || tabChanged)),
      withLatestFrom(
        this.dataSelectionService.selectedProject,
        this.dataSelectionService.timeSelection,
        timeSelectionItems$
      ),
      tap(([{ tab, loadedChanged }, project, timeSelection, timeSelectionItems]) => {
        const displayKey = tab && this.tabDisplayKeyMap[tab];

        if (!displayKey) {
          this.dataSelectionService.setTimeSelection(null);
          return;
        }

        const selections =
          loadedChanged || !timeSelection
            ? this.getDefaultTimeSelectionItems(project, timeSelectionItems)
            : timeSelection.selections;

        this.dataSelectionService.setTimeSelection(selections.length ? { displayKey, selections } : null);
      })
    );

    return merge(syncItems$, tabSelection$);
  }

  public getActualLdLogger(ldLoggers: ILdLogger[]): ILdLogger | undefined {
    return this.getLatestByIsoDate(ldLoggers, logger => logger.measurement?.measStartTime);
  }

  public getActualMeasurement(measurements: ILoggerMeasurement[]): ILoggerMeasurement | undefined {
    return this.getLatestByIsoDate(measurements, measurement => measurement.measStartTime);
  }

  private getLatestByIsoDate<T>(items: T[], getDate: (item: T) => string | undefined): T | undefined {
    return items.reduce<T | undefined>((latest, current) => {
      const currentTime = getDate(current);
      const latestTime = latest ? getDate(latest) : undefined;

      if (!currentTime) {
        return latest;
      }

      if (!latestTime) {
        return current;
      }

      return currentTime > latestTime ? current : latest;
    }, undefined);
  }

  private isRelatedToSelectedTime(
    tabTimesMap: ITabTimesMap,
    time: string | undefined,
    tab: LdTabs,
    emptyTimeKey?: string
  ): boolean {
    const normalizedTime = time || emptyTimeKey;
    if (!normalizedTime) return true;

    return tabTimesMap[tab]?.has(normalizedTime) ?? true;
  }

  private buildTabTimesMap(timeSelection: ITimeSelection | null): ITabTimesMap | null {
    if (!timeSelection?.selections.length) return null;

    return timeSelection.selections.reduce((acc, selection: ITimeSelectionItem) => {
      const soundSignalStartTime = selection.data[TimeSelectionKey.SoundSignalStartTime];
      const measStartTime = selection.data[TimeSelectionKey.MeasStartTime];

      (acc[LdTabs.Correlations] ??= new Set<string>()).add(soundSignalStartTime || NO_SOUND_SIGNAL_KEY);
      if (measStartTime) (acc[LdTabs.LoggerNoise] ??= new Set<string>()).add(measStartTime);

      return acc;
    }, {} as ITabTimesMap);
  }

  private buildTimeSelectionItems(measurements: ILoggerMeasurement[]): ITimeSelectionItem[] {
    const measStartTimesWithSoundSignal = new Set(
      measurements.filter(lm => lm.measStartTime && lm.soundSignalStartAt).map(lm => lm.measStartTime!)
    );

    const itemsByKey = measurements.reduce((acc, lm) => {
      if (!lm.measStartTime) return acc;

      if (!lm.soundSignalStartAt && measStartTimesWithSoundSignal.has(lm.measStartTime)) return acc;

      const measStartTime = lm.measStartTime;
      const soundSignalStartTime = lm.soundSignalStartAt;
      acc.set(`${measStartTime}_${soundSignalStartTime || NO_SOUND_SIGNAL_KEY}`, {
        measStartTime,
        soundSignalStartTime,
      });

      return acc;
    }, new Map<string, { measStartTime: string; soundSignalStartTime: string | undefined }>());

    return [...itemsByKey.values()]
      .sort((a, b) => a.measStartTime.localeCompare(b.measStartTime))
      .map((item, index) => ({ id: `${index}`, data: item }));
  }

  private getDefaultTimeSelectionItems(
    project: IProject | undefined,
    timeSelectionItems: ITimeSelectionItem[]
  ): ITimeSelectionItem[] {
    if (timeSelectionItems.length <= 1) {
      return [];
    }

    if (isMixedOperatingType(project?.operatingType)) {
      return timeSelectionItems;
    }

    const lastTimeSelectionItem = timeSelectionItems.at(-1);

    return lastTimeSelectionItem ? [lastTimeSelectionItem] : [];
  }
}
